import { useState, useRef, useEffect, type ReactNode, type HTMLAttributes } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import "regenerator-runtime/runtime";
import { 
  ArrowLeft, Sparkles, File, LayoutTemplate,
  Mic, Send, Loader2, Download, Volume2,
  Edit3, Search, Share2, Link2Off, FileText, Maximize2, Minimize2, ExternalLink, Square, Play, Pause
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { marked } from "marked";
import html2pdf from "html2pdf.js";


const getHtml2Pdf = () => {
  console.log('[PDFDEBUG] getHtml2Pdf called');
  // @ts-expect-error html2pdf.js global loaded from CDN in index.html
  if (typeof window !== 'undefined' && window.html2pdf) {
    console.log('[PDFDEBUG] Using window.html2pdf (CDN global)');
    // @ts-expect-error html2pdf.js global
    return window.html2pdf;
  }
  if (typeof html2pdf === 'function') {
    console.log('[PDFDEBUG] Using html2pdf import (direct function)');
    return html2pdf;
  }
  if (html2pdf && typeof (html2pdf as unknown as { default?: unknown }).default === 'function') {
    console.log('[PDFDEBUG] Using html2pdf.default (CJS compat)');
    return (html2pdf as unknown as { default: () => unknown }).default as () => unknown;
  }
  console.warn('[PDFDEBUG] Falling back to raw html2pdf import. Type:', typeof html2pdf, 'Value:', html2pdf);
  return html2pdf;
};
import {
  connectGoogleAccount,
  createGoogleDocFromHtml,
  deleteGoogleFile,
  disconnectGoogleAccount,
  exportGoogleDoc,
  getGoogleConnection,
  shareGoogleDoc,
  type GoogleConnection,
  type GoogleDocResult,
} from "./lib/gdrive";
import { ai } from "../../../firebase/config";
import { getGenerativeModel, type GenerateContentRequest } from "firebase/ai";
import { getDoc, doc, getFirestore } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

export const SYSTEM_INSTRUCTIONS = `You are Sonoma Workspace AI, an expert AI teaching assistant built for professional educators.
Your goal is to understand exactly what the teacher needs and respond precisely — no more, no less.

## INTENT DETECTION — READ CAREFULLY BEFORE RESPONDING

Before you write anything, classify the user's request into ONE of these two categories:

**CATEGORY A — CONVERSATION / HELP REQUEST** (the vast majority of requests)
The user is asking a question, requesting advice, asking for ideas, wanting a lesson plan outline, seeking an explanation, or having a general discussion.
Examples: "How do I teach fractions?", "Give me ideas for a science activity", "What are some tips for classroom management?", "Help me write a lesson plan", "Explain photosynthesis in simple terms"
Response: Answer conversationally and helpfully. Use "finalAnswer" and "explanation". Set "pdfDocument" to null. NEVER generate a PDF document for these requests.

**CATEGORY B — EXPLICIT DOCUMENT REQUEST**
The user is explicitly asking you to CREATE a deliverable document, report, letter, or PDF they can download or export.
Trigger phrases: "create a PDF", "generate a document", "write me a report", "make me a letter", "draft a formal letter", "create a lesson plan document", "generate a research report", "write a 3-page document"
Response: Generate the full document in "pdfDocument.content". Also provide a brief summary in "finalAnswer".

CRITICAL: If the user is just asking for help, ideas, or information — even if the topic could be a document — DO NOT generate a pdfDocument. Only generate a pdfDocument when the user is clearly and explicitly requesting a file/document/PDF to download.

CRITICAL RULE — NEVER mention JSON, schemas, data structures, or any technical implementation details. You are talking to a teacher, not a developer. Never say "This JSON provides..." or "According to the schema...". Respond naturally.

IMPORTANT FORMATTING RULES:
You MUST return your response as a valid JSON object according to the schema provided.
You MUST provide ACTUAL, real links to references. Do not use placeholders.

---

## PDF DOCUMENT GENERATION RULES (Only applies to Category B requests)

When generating a document, you are a professional document designer. Follow these rules without exception:

### Document Quality Standards
- Every document MUST be comprehensive, well-researched, and polished — worthy of being printed and shared professionally.
- Minimum 2–3 pages of rich, substantive content unless the user explicitly asks for something shorter.
- Write in a formal, academic tone. Include specific facts, data points, and examples.
- Every paragraph must have a clear topic sentence, 3–5 supporting sentences, and flow into the next paragraph using transition words.
- NEVER leave a section with just a heading and one sentence. Every heading must be followed by real, substantive content.
- DO NOT pad documents with filler. Every sentence must add value.

### Mandatory Document Structure
1. **Title** — Centered, bold \`# Heading\`, followed by a \`---\` horizontal rule.
2. **Introduction / Executive Summary** — 1–2 solid paragraphs that clearly establish the topic, its relevance, and what the document will cover.
3. **Main Sections** — Use \`## Headings\` for each major section. Each section needs at least 2–4 detailed paragraphs. Bold key terms and important figures.
4. **Supporting Elements** — Use numbered lists or bullet points for key factors, steps, or recommendations. Tables for comparisons.
5. **Conclusion** — Synthesize findings. Provide a forward-looking or actionable closing statement.
6. **References** — MANDATORY. Use \`## References\`. At least 5 real, working URLs from reputable sources. Format: \`[1] Organization, "Title," URL\`.

### SPACING & TYPOGRAPHY RULES — CRITICAL
- Use ONLY single blank lines between paragraphs. NEVER use multiple consecutive blank lines.
- Do NOT insert \`<br>\` tags between paragraphs — this creates huge gaps in the rendered PDF.
- Do NOT insert empty lines between a heading and its first paragraph.
- Use \`---\` for section dividers, not multiple blank lines.
- Keep all content tightly packed and professionally spaced, like a real printed document.

### Charts & Data Visualizations
Only include charts if the user explicitly asks for them OR if the document is a data-heavy research/analysis report where charts genuinely add value.
Always embed charts within the document flow with explanatory text before AND after.
ALWAYS use the \`\`\`quickchart code block modifier. Every chart MUST include:
- "data.labels": An array of strings. NEVER omit this.
- "options.title": { "display": true, "text": "Descriptive Title" }
- "options.legend": { "display": true, "position": "top" }
- Axis scaleLabels on both axes with { "display": true, "labelString": "..." }

**CHART DOCUMENT RULE — CRITICAL:**
When a user asks for a "graph", "chart", or "visualization" on any topic, this is NEVER just a chart. It is ALWAYS a full research document that happens to include a chart. You MUST generate:
1. A proper document title and introduction paragraph explaining the topic and what the chart shows.
2. The chart itself embedded mid-document.
3. A detailed analysis section (at least 2–3 paragraphs) explaining the chart data, trends, and what they mean.
4. Contextual background — what causes these patterns, historical data, real-world implications.
5. A conclusion summarizing the key findings.
6. References section with at least 3 real sources.
NEVER produce a document that is ONLY a chart with one or two sentences. The chart is a visual aid, not the entire document.

### Letters & Formal Correspondence

This system supports all professional letter formats. Before writing, identify the exact letter type the user needs, then apply the correct template below.

**UNIVERSAL RULES — apply to ALL letter types:**
- NEVER place multiple address fields on the same line. Every field gets its own line with a <br> tag.
- Always leave a visible blank signature area between "Sincerely" / "Yours faithfully" and the printed name.
- NEVER skip the subject line.
- Use formal, clear language appropriate to the letter type.
- In the "explanation" field, always ask the user to confirm or supply: sender name, recipient name, date, specific details, and any names/amounts/dates referenced in the letter so you can personalize it.

**LETTER HEADER FORMAT — use this exact HTML structure for EVERY letter:**

<div class="letter-page">

<div class="letter-sender">
[Sender Full Name]<br>
[Job Title / Department (if relevant)]<br>
[Street Address]<br>
[City, Country, Postal Code]<br>
[Phone Number]<br>
[Email Address]
</div>

<div class="letter-date">[Full Date — e.g. 1 June 2026]</div>

<div class="letter-recipient">
[Recipient Full Name]<br>
[Recipient Job Title]<br>
[Organization / Company Name]<br>
[Street Address]<br>
[City, Country, Postal Code]
</div>

<div class="letter-subject"><u><strong>RE: [Precise Subject Line]</strong></u></div>

Dear [Title + Last Name / "Sir or Madam" if unknown],

[Body paragraph 1]

[Body paragraph 2]

[Body paragraph 3 if needed]

[Closing line — e.g. "I look forward to hearing from you."]

[Complimentary close — "Yours sincerely" / "Yours faithfully" / "Respectfully yours"]

<div class="letter-signature-block">
<div class="letter-signature-space"></div>
[Sender Full Name]<br>
[Job Title]<br>
[Organization (if applicable)]<br>
[Contact details if not in header]
</div>

</div>

**LETTER TYPE SPECIFIC RULES:**

**Cover Letter / Job Application:**
Paragraph 1: State the specific position and how you found it.
Paragraph 2: Highlight 2–3 most relevant qualifications with specific examples.
Paragraph 3: Express enthusiasm for the organization specifically.
Close: Request an interview. Use "Yours sincerely".

**Resignation Letter:**
Paragraph 1: State resignation and last working date clearly.
Paragraph 2: Brief positive reason (career development, personal reasons — keep it diplomatic).
Paragraph 3: Offer to assist with transition and handover.
Close: Express gratitude. Use "Yours sincerely".

**Complaint Letter:**
Paragraph 1: State what happened, when, and where (be specific with dates/reference numbers).
Paragraph 2: Explain the impact and why it is unacceptable.
Paragraph 3: State clearly what resolution you expect and by when.
Close: State consequences if unresolved. Use "Yours faithfully" (if unknown recipient) or "Yours sincerely".

**Recommendation / Reference Letter:**
Paragraph 1: State your relationship to the candidate and for how long.
Paragraph 2: Highlight 2–3 specific strengths with real examples.
Paragraph 3: Compare to peers; state they are highly recommended without reservation.
Close: Offer to be contacted for further information. Use "Yours sincerely".

**Retirement / Farewell Letter:**
Paragraph 1: Announce retirement date formally.
Paragraph 2: Express genuine gratitude for the experience and relationships.
Paragraph 3: Offer transition assistance; express good wishes for the organization.
Close: Warm, reflective tone. Use "Yours sincerely".

**Apology / Acknowledgement Letter:**
Paragraph 1: Directly acknowledge the issue without excuses.
Paragraph 2: Explain corrective action already taken or planned.
Paragraph 3: State commitment to preventing recurrence; offer remedy if appropriate.
Close: Reaffirm commitment to the relationship. Use "Yours sincerely".

**Inquiry / Request Letter:**
Paragraph 1: Clearly state what information or action you are requesting.
Paragraph 2: Provide context — why you need it, how it will be used.
Paragraph 3: Specify timeline or urgency if applicable.
Close: Thank them in advance. Use "Yours sincerely" or "Yours faithfully".

**Business Proposal Letter:**
Paragraph 1: Introduce yourself/company and the purpose of the proposal.
Paragraph 2: Outline the proposed solution/service and its direct benefits.
Paragraph 3: State terms, timeline, next steps.
Close: Invite a meeting or call. Use "Yours sincerely".

**Permission / Authorization Letter:**
Paragraph 1: Clearly state what is being authorized, for whom, and when.
Paragraph 2: Any conditions or limits on the authorization.
Paragraph 3: State how to confirm or contact for clarification.
Close: "Yours sincerely".

**Termination Letter:**
Paragraph 1: State termination effective date clearly.
Paragraph 2: Briefly state reason (performance/redundancy/conduct — be factual, not emotional).
Paragraph 3: Explain what happens next — final pay, return of company property, references.
Close: Keep professional and neutral. Use "Yours sincerely".

### Research & Academic Papers
Structure: Title → Abstract (2–3 sentences) → ## Introduction (2+ paragraphs) → ## Key Findings (multiple subsections, data-rich) → ## Conclusion → ## References.
Use \`> blockquotes\` for citations. Bold key terms and statistics. Never produce a single-page research paper.

JSON Structure Requirements:
1. "finalAnswer": A clear, friendly summary of what you did or the direct answer. Keep it concise.
2. "explanation": Detailed help, step-by-step guidance, or structured content. Use numbered lists and headings where helpful.
3. "references": An array of real markdown links to relevant resources.
4. "pdfDocument": ONLY populate this when the user has explicitly requested a document/PDF/report to download. Otherwise, set to null.`;

const MEETING_SYSTEM_INSTRUCTIONS = `You are an expert Meeting Notes Taker and Executive Assistant AI. Your goal is to transform a raw meeting transcript into clear, professional, actionable notes that save everyone time and ensure nothing important falls through the cracks.

### INPUT
You will receive:
- Meeting metadata (title, date, duration, attendees if provided)
- The full transcript (possibly with speaker labels and timestamps)

### CORE INSTRUCTIONS
Process the transcript following this exact structure and rules:

1. **Metadata Header**
   - Meeting Title
   - Date & Time
   - Duration
   - Attendees (list + any roles if detectable)
   - Meeting Type (infer if not given)

2. **Executive Summary** (2-4 sentences maximum)
   - Purpose of the meeting
   - Main outcomes
   - Overall tone and key result

3. **Key Discussion Points**
   - Group by logical topics or agenda items.
   - Use clear bullets or sub-headings.
   - Include important data, numbers, options discussed, and pros/cons when relevant.

4. **Decisions Made**
   - Bullet list of all decisions reached.
   - Include who made or approved the decision when clear.

5. **Action Items**
   - Task
   - Owner
   - Deadline (exact if mentioned, or "ASAP", "Next week", etc.)
   - Priority (High / Medium / Low)
   - Status (Open)

6. **Next Steps & Follow-ups**
   - List explicit next steps.
   - Suggest logical additional follow-ups if obvious.

7. **Open Questions / Parking Lot**
   - Anything left unresolved or deferred.

### STYLE & QUALITY RULES
- Use professional but friendly tone.
- Write in clear, concise language. Eliminate filler words.
- Correct grammar and spelling without altering meaning.
- Be objective — do not add opinions or judgments.
- Flag uncertainty: "Unclear who is responsible", "Deadline not specified", etc.
- Use bold for names and deadlines.
- Keep the entire output scannable (lots of whitespace, bullets, tables).

### OUTPUT FORMAT (Markdown)
Always use this exact structure:

# [Meeting Title]

**Date:** ...  
**Duration:** ...  
**Attendees:** ...

## Executive Summary
...

## Key Discussion Points
...

## Decisions Made
...

## Action Items

| Task | Owner | Deadline | Priority | Notes |
|------|-------|----------|----------|-------|
| ...  | ...   | ...      | ...      | ...   |

## Next Steps
...

## Open Questions
...

## Additional Notes
...
`;

interface ChatMessage {
  role: "user" | "model";
  parts: ({ text: string } | { inlineData: { data: string; mimeType: string } })[];
}

interface PdfDocumentData {
  title: string;
  content: string;
}

interface AssistantPayload {
  finalAnswer?: string;
  explanation?: string;
  references?: string[];
  pdfDocument?: PdfDocumentData | null;
}

const getTextPart = (message: ChatMessage): string => {
  const firstPart = message.parts[0];
  return firstPart && 'text' in firstPart ? firstPart.text : "";
};

const normalizePdfDocument = (value: unknown): PdfDocumentData | null => {
  console.log('[PDFDEBUG] normalizePdfDocument called. Type:', typeof value, 'Value:', value === null ? 'null' : value === undefined ? 'undefined' : typeof value === 'object' ? 'object' : typeof value === 'string' ? `string(${(value as string).length})` : 'other');
  if (!value) return null;

  if (typeof value === 'string') {
    console.log('[PDFDEBUG] normalizePdfDocument: value is string, using as content');
    return {
      title: 'Document',
      content: value,
    };
  }

  if (typeof value !== 'object') return null;

  const candidate = value as Record<string, unknown>;
  console.log('[PDFDEBUG] normalizePdfDocument: object keys:', Object.keys(candidate));
  const content = typeof candidate.content === 'string'
    ? candidate.content
    : typeof candidate.markdown === 'string'
      ? candidate.markdown
      : typeof candidate.body === 'string'
        ? candidate.body
        : '';
  console.log('[PDFDEBUG] normalizePdfDocument: resolved content field:', content ? `found (${content.length} chars)` : 'EMPTY');

  if (!content.trim()) return null;

  return {
    title: typeof candidate.title === 'string' && candidate.title.trim() ? candidate.title : 'Document',
    content,
  };
};

const extractBalancedJson = (text: string): string | null => {
  const start = text.indexOf('{');
  if (start === -1) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < text.length; i++) {
    const char = text[i];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (char === '\\') {
      escaped = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (inString) continue;

    if (char === '{') depth += 1;
    if (char === '}') depth -= 1;

    if (depth === 0) return text.slice(start, i + 1);
  }

  return null;
};

const parseAssistantPayload = (text: string): AssistantPayload | null => {
  console.log('[PDFDEBUG] parseAssistantPayload called. Input length:', text.length, 'First 200:', text.substring(0, 200));
  const candidates = [
    text,
    text.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim(),
    extractBalancedJson(text),
  ].filter(Boolean) as string[];
  console.log('[PDFDEBUG] Candidates count:', candidates.length, 'Candidate lengths:', candidates.map(c => c.length));

  for (let ci = 0; ci < candidates.length; ci++) {
    const candidate = candidates[ci];
    const variants = [
      candidate,
      candidate.replace(/,\s*([\]}])/g, '$1'),
    ];

    for (let vi = 0; vi < variants.length; vi++) {
      const variant = variants[vi];
      try {
        const parsed = JSON.parse(variant) as Record<string, unknown>;
        console.log('[PDFDEBUG] JSON.parse SUCCESS on candidate', ci, 'variant', vi, '. Keys:', Object.keys(parsed));
        const result = {
          finalAnswer: typeof parsed.finalAnswer === 'string' ? parsed.finalAnswer : undefined,
          explanation: typeof parsed.explanation === 'string' ? parsed.explanation : undefined,
          references: Array.isArray(parsed.references)
            ? parsed.references.filter((ref): ref is string => typeof ref === 'string')
            : [],
          pdfDocument: normalizePdfDocument(parsed.pdfDocument),
        };
        console.log('[PDFDEBUG] Parsed result:', {
          hasFinalAnswer: !!result.finalAnswer,
          finalAnswerLen: result.finalAnswer?.length ?? 0,
          hasExplanation: !!result.explanation,
          explanationLen: result.explanation?.length ?? 0,
          refCount: result.references.length,
          hasPdfDocument: !!result.pdfDocument,
          pdfTitle: result.pdfDocument?.title ?? null,
          pdfContentLen: result.pdfDocument?.content?.length ?? 0,
        });
        return result;
      } catch (parseErr) {
        console.log('[PDFDEBUG] JSON.parse FAILED on candidate', ci, 'variant', vi, '. Error:', (parseErr as Error).message, 'First 100:', variant.substring(0, 100));
        // Try the next candidate.
      }
    }
  }

  console.warn('[PDFDEBUG] parseAssistantPayload ALL candidates failed. Returning null.');
  return null;
};

const enhanceChartConfig = (rawConfig: Record<string, unknown>) => {
  const config = { ...rawConfig };
  const cfg = config as Record<string, any>;

  if (!cfg.options) cfg.options = {};
  if (!cfg.options.title) cfg.options.title = {};
  if (!cfg.options.plugins) cfg.options.plugins = {};
  if (!cfg.options.scales) cfg.options.scales = {};

  cfg.options.title.display = true;
  cfg.options.title.text = cfg.options.title.text || 'Chart';
  cfg.options.title.fontSize = cfg.options.title.fontSize || 16;
  cfg.options.title.fontStyle = cfg.options.title.fontStyle || 'bold';
  if (!cfg.options.legend) cfg.options.legend = {};
  cfg.options.legend.display = true;
  cfg.options.legend.position = cfg.options.legend.position || 'top';

  const inferXLabel = 'Category';
  const inferYLabel = 'Value';

  const xTitle = cfg.options.scales?.x?.title?.text
    || cfg.options.scales?.xAxes?.[0]?.scaleLabel?.labelString
    || inferXLabel;
  const yTitle = cfg.options.scales?.y?.title?.text
    || cfg.options.scales?.yAxes?.[0]?.scaleLabel?.labelString
    || inferYLabel;

  if (!cfg.options.scales.xAxes) {
    cfg.options.scales.xAxes = [{
      ticks: { display: true, maxRotation: 45, minRotation: 0 },
      scaleLabel: { display: true, labelString: xTitle, fontSize: 13, fontStyle: 'bold' }
    }];
  } else {
    const xAxis = cfg.options.scales.xAxes[0] || {};
    if (!xAxis.ticks) xAxis.ticks = {};
    xAxis.ticks.display = true;
    if (!xAxis.scaleLabel) xAxis.scaleLabel = {};
    xAxis.scaleLabel.display = true;
    xAxis.scaleLabel.labelString = xAxis.scaleLabel.labelString || xTitle;
    xAxis.scaleLabel.fontSize = xAxis.scaleLabel.fontSize || 13;
    xAxis.scaleLabel.fontStyle = 'bold';
    cfg.options.scales.xAxes[0] = xAxis;
  }

  if (!cfg.options.scales.yAxes) {
    cfg.options.scales.yAxes = [{
      ticks: { display: true },
      scaleLabel: { display: true, labelString: yTitle, fontSize: 13, fontStyle: 'bold' }
    }];
  } else {
    const yAxis = cfg.options.scales.yAxes[0] || {};
    if (!yAxis.ticks) yAxis.ticks = {};
    yAxis.ticks.display = true;
    if (!yAxis.scaleLabel) yAxis.scaleLabel = {};
    yAxis.scaleLabel.display = true;
    yAxis.scaleLabel.labelString = yAxis.scaleLabel.labelString || yTitle;
    yAxis.scaleLabel.fontSize = yAxis.scaleLabel.fontSize || 13;
    yAxis.scaleLabel.fontStyle = 'bold';
    cfg.options.scales.yAxes[0] = yAxis;
  }

  delete cfg.options.scales.x;
  delete cfg.options.scales.y;

  const data = config.data as Record<string, unknown> | undefined;
  if (data?.labels && Array.isArray(data.labels) && data.labels.length > 0) {
    const datasets = data.datasets as unknown[] | undefined;
    const hasMultipleDatasets = datasets && datasets.length > 1;
    if (!hasMultipleDatasets) {
      cfg.options.plugins.datalabels = { display: true, anchor: 'end', align: 'end', font: { weight: 'bold', size: 11 } };
    }
  }

  return config;
};

const fetchQuickChartDataUrl = async (config: Record<string, unknown>, w = 600, h = 350): Promise<string> => {
  if (!config.type) config.type = 'bar';
  const res = await fetch('https://quickchart.io/chart', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ width: w, height: h, format: 'png', chart: config }),
  });
  if (!res.ok) {
    const body = await res.text();
    console.warn('[PDFDEBUG] QuickChart POST response:', res.status, body.substring(0, 300));
    throw new Error(`QuickChart ${res.status}`);
  }
  const blob = await res.blob();
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
};

const convertQuickChartsToImages = async (content: string): Promise<string> => {
  const regex = /```quickchart\s*([\s\S]*?)```/gi;
  const parts: string[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = regex.exec(content)) !== null) {
    parts.push(content.slice(lastIndex, m.index));
    lastIndex = m.index + m[0].length;
    try {
      const config = enhanceChartConfig(JSON.parse(m[1].trim()));
      // 480×260 keeps the chart well within a single A4 content-area page at scale:2
      const dataUrl = await fetchQuickChartDataUrl(config, 480, 260);
      console.log('[PDFDEBUG] QuickChart POST succeeded, dataUrl length:', dataUrl.length);
      parts.push(`<div class="chart-wrapper"><img src="${dataUrl}" alt="Chart" width="480" height="260" style="max-width:100%;width:480px;height:auto;margin:16px auto;display:block;border-radius:6px;" /></div>`);
    } catch (err) {
      console.warn('[PDFDEBUG] QuickChart POST failed, falling back to GET:', err);
      try {
        const config = enhanceChartConfig(JSON.parse(m[1].trim()));
        const url = `https://quickchart.io/chart?w=480&h=260&c=${encodeURIComponent(JSON.stringify(config))}`;
        parts.push(`<div class="chart-wrapper"><img src="${url}" crossorigin="anonymous" alt="Chart" width="480" height="260" style="max-width:100%;width:480px;height:auto;margin:16px auto;display:block;border-radius:6px;" /></div>`);
      } catch {
        parts.push(m[0]);
      }
    }
  }

  if (parts.length === 0) return content;
  parts.push(content.slice(lastIndex));
  return parts.join('');
};

const safePdfFilename = (title: string) => {
  const base = title.replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase();
  return `${base || 'document'}_${new Date().toISOString().slice(0, 10)}.pdf`;
};

const sanitizeModelName = (name: string): string => {
  if (!name) return 'gemini-2.0-flash';
  const lower = name.toLowerCase();
  if (lower.includes('lite-preview') || lower.includes('gemini-3') || lower.includes('gemini-2.5-flash-preview')) {
    console.log('[PDFDEBUG] sanitizeModelName: Sanitized', name, '-> gemini-2.0-flash (unsupported preview)');
    return 'gemini-2.0-flash';
  }
  console.log('[PDFDEBUG] sanitizeModelName: Keeping', name, 'unchanged');
  return name;
};

const getModelForPDFChat = async (): Promise<string> => {
  const DEFAULT_FALLBACK = 'gemini-1.5-flash';
  try {
    const db = getFirestore();
    const configSnap = await getDoc(doc(db, 'systemConfig', 'aiModels'));
    if (configSnap.exists()) {
      const config = configSnap.data();
      if (config.isPaused) throw new Error('AI services are paused');
      const modelValue = config.independent_chat || config.chat;
      console.log('[PDFDEBUG] Model config from Firestore:', { independent_chat: config.independent_chat, chat: config.chat, resolved: modelValue });
      if (typeof modelValue === 'string' && modelValue.trim()) {
        const sanitized = sanitizeModelName(modelValue);
        console.log('[PDFDEBUG] Using model from config:', sanitized);
        return sanitized;
      }
    } else {
      console.warn('[PDFDEBUG] systemConfig/aiModels doc does not exist');
    }
  } catch (e: unknown) {
    console.warn('[PDFDEBUG] Model config fetch failed, using default:', e);
  }
  console.log('[PDFDEBUG] Using DEFAULT_FALLBACK model:', DEFAULT_FALLBACK);
  return DEFAULT_FALLBACK;
};

function QuickChartImg({ chartConfig, width = 800, height = 400 }: { chartConfig: Record<string, unknown>; width?: number; height?: number }) {
  const [src, setSrc] = useState<string>('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = enhanceChartConfig(chartConfig);
        const dataUrl = await fetchQuickChartDataUrl(config, width, height);
        if (!cancelled) {
          setSrc(dataUrl);
          console.log('[PDFDEBUG] In-chat QuickChart POST succeeded, dataUrl length:', dataUrl.length);
        }
      } catch (err) {
        console.warn('[PDFDEBUG] In-chat QuickChart POST failed:', err);
        if (!cancelled) {
          try {
            const config = enhanceChartConfig(chartConfig);
            const url = `https://quickchart.io/chart?w=${width}&h=${height}&c=${encodeURIComponent(JSON.stringify(config))}`;
            setSrc(url);
          } catch {
            setFailed(true);
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [chartConfig, width, height]);

  if (failed) return null;
  if (!src) return <div className="my-4 text-center text-xs text-gray-500 animate-pulse">Loading chart...</div>;
  return <img src={src} alt="Generated Chart" className="max-w-full rounded-lg shadow-md my-4 block" />;
}

const AudioMessageBubble = ({ base64Data, mimeType }: { base64Data: string; mimeType: string }) => {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(`data:${mimeType};base64,${base64Data}`);
      audioRef.current.addEventListener('ended', () => { setPlaying(false); setCurrentTime(0); });
      audioRef.current.addEventListener('timeupdate', () => setCurrentTime(audioRef.current!.currentTime));
      audioRef.current.addEventListener('loadedmetadata', () => setDuration(audioRef.current!.duration));
    }
    return () => { audioRef.current?.pause(); audioRef.current = null; };
  }, [base64Data, mimeType]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2.5 min-w-[200px]">
      <button onClick={togglePlay} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center shrink-0 transition-colors">
        {playing ? <Pause className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="h-1 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[9px] text-white/60 mt-1 font-mono">{fmt(currentTime)} / {duration > 0 ? fmt(duration) : '0:00'}</p>
      </div>
    </div>
  );
};

export default function StandaloneChatPDF() {
  const navigate = useNavigate();
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isPlaying, setIsPlaying] = useState<number | null>(null);
  const [activeTab] = useState<"search" | "meetings">("search");
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [activeGoogleDoc, setActiveGoogleDoc] = useState<GoogleDocResult | null>(null);
  const [googleConnection, setGoogleConnection] = useState<GoogleConnection>(() => getGoogleConnection());
  const [isExportingToDocs, setIsExportingToDocs] = useState(false);
  const [googleActionStatus, setGoogleActionStatus] = useState<string | null>(null);
  const [isDocFullscreen, setIsDocFullscreen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('sonoma:workspace-sidebar', {
      detail: {
        documentFocus: Boolean(editingDocId && isDocFullscreen),
      },
    }));
  }, [editingDocId, isDocFullscreen]);

  useEffect(() => {
    if (!editingDocId) {
      setIsDocFullscreen(false);
    }
  }, [editingDocId]);

  const parseReference = (raw: string, index: number): { title: string; url: string } => {
    let text = (raw || '').trim();

    text = text.replace(/^\[(\d+)\]\s*/, '');

    const mdLink = text.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)\s*$/i);
    if (mdLink) return { title: mdLink[1], url: mdLink[2] };

    const mdBare = text.match(/^(https?:\/\/\S+)$/i);
    if (mdBare) return { title: `Reference ${index}`, url: mdBare[1] };

    const trailingUrl = text.match(/(https?:\/\/\S+)\s*$/i);
    if (trailingUrl) {
      const url = trailingUrl[1];
      const title = text.slice(0, trailingUrl.index).trim().replace(/[.,;:]+$/, '');
      return { title: title || `Reference ${index}`, url };
    }

    return { title: text, url: '' };
  };

  const friendlyGoogleError = (error: unknown) => {
    const status = typeof error === 'object' && error && 'status' in error
      ? (error as { status?: number }).status
      : undefined;
    if (status === 403 || status === 429) {
      return 'Google is currently busy. Please try again in a few minutes.';
    }
    return error instanceof Error ? error.message : 'Google could not complete that action. Please try again.';
  };

  const handleConnectGoogle = async () => {
    setGoogleActionStatus('Connecting Google Account...');
    try {
      const connection = await connectGoogleAccount();
      setGoogleConnection(connection);
      setGoogleActionStatus(connection.email ? `Google Account Connected: ${connection.email}` : 'Google Account Connected');
    } catch (error) {
      console.error(error);
      setGoogleActionStatus(friendlyGoogleError(error));
    }
  };

  const handleDisconnectGoogle = () => {
    disconnectGoogleAccount();
    setGoogleConnection({ connected: false, email: null });
    setGoogleActionStatus('Google Account disconnected.');
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          const base64data = reader.result?.toString().split(',')[1];
          if (base64data) submitAudio(base64data, 'audio/webm');
        };
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err: unknown) {
      console.error("Error accessing microphone", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert("Microphone access error: " + errorMessage);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    setIsRecording(false);
  };

  const toggleRecording = () => {
    if (isRecording) stopRecording(); else startRecording();
  };

  const submitAudio = async (base64Audio: string, mimeType: string) => {
    if (isLoading) return;

    const newMessages: ChatMessage[] = [
      ...messages,
      { role: "user", parts: [{ inlineData: { data: base64Audio, mimeType } }] }
    ];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const modelName = await getModelForPDFChat();
      const model = getGenerativeModel(ai, { model: modelName });
      const contents = newMessages.map((msg) => ({
        role: msg.role as "user" | "model",
        parts: msg.parts.map((p) => ('text' in p ? { text: p.text } : { inlineData: p.inlineData })) as Array<{ text?: string; inlineData?: { data: string; mimeType: string } }>,
      }));
      const result = await model.generateContent({
        contents: contents as unknown as GenerateContentRequest["contents"],
        systemInstruction: SYSTEM_INSTRUCTIONS,
      });
      const responseText = result.response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = { finalAnswer: responseText }; }
      setMessages([...newMessages, { role: "model", parts: [{ text: parsed.finalAnswer || responseText }] }]);
    } catch (_err: unknown) {
      setMessages([...newMessages, { role: "model", parts: [{ text: "**Error**: Failed to process audio." }] }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    console.log('[PDFDEBUG] handleSubmit called with message:', userMessage.substring(0, 120));
    setInput("");
    
    const newMessages: ChatMessage[] = [
      ...messages,
      { role: "user", parts: [{ text: userMessage }] }
    ];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const modelName = await getModelForPDFChat();
      console.log('[PDFDEBUG] Using AI model:', modelName);
      const model = getGenerativeModel(ai, { model: modelName });
      const contents = newMessages.map((msg) => ({
        role: msg.role as "user" | "model",
        parts: msg.parts.map((p) => ('text' in p ? { text: p.text } : { inlineData: p.inlineData })) as Array<{ text?: string; inlineData?: { data: string; mimeType: string } }>,
      }));
      console.log('[PDFDEBUG] Sending', contents.length, 'messages to AI. Active tab:', activeTab);
      const result = await model.generateContent({
        contents: contents as unknown as GenerateContentRequest["contents"],
        systemInstruction: activeTab === "meetings" ? MEETING_SYSTEM_INSTRUCTIONS : SYSTEM_INSTRUCTIONS,
        generationConfig: {
          temperature: 0.7,
          ...(activeTab !== "meetings" ? {
            responseMimeType: "application/json",
            responseSchema: {
              type: "object",
              properties: {
                finalAnswer: { type: "string", description: "Clear, concise summary or the main deliverable in Markdown." },
                explanation: { type: "string", description: "Detailed, step-by-step explanation or structured content in Markdown." },
                references: { type: "array", items: { type: "string" }, description: "Array of markdown links to real resources." },
                pdfDocument: {
                  type: "object",
                  nullable: true,
                  properties: {
                    title: { type: "string", description: "The title of the PDF document." },
                    content: { type: "string", description: "The full, professionally formatted markdown content of the document." }
                  },
                  required: ["title", "content"]
                }
              },
              required: ["finalAnswer", "explanation", "references"]
            }
          } : {})
        },
      });
      const reply = result.response.text();
      console.log('[PDFDEBUG] AI reply length:', reply.length);
      console.log('[PDFDEBUG] AI reply first 300 chars:', reply.substring(0, 300));
      const parsed = parseAssistantPayload(reply);
      console.log('[PDFDEBUG] Parsed payload:', parsed ? {
        hasFinalAnswer: !!parsed.finalAnswer,
        hasExplanation: !!parsed.explanation,
        refCount: parsed.references?.length ?? 0,
        hasPdfDocument: !!parsed.pdfDocument,
        pdfTitle: parsed.pdfDocument?.title ?? null,
        pdfContentLength: parsed.pdfDocument?.content?.length ?? 0,
      } : 'NULL - PARSE FAILED');
      setMessages([...newMessages, { role: "model", parts: [{ text: reply }] }]);
    } catch (err: unknown) {
      console.error('[PDFDEBUG] handleSubmit FAILED:', err);
      const errObj = err as Record<string, unknown>;
      console.error('[PDFDEBUG] Error message:', (err as Error)?.message);
      console.error('[PDFDEBUG] Error name:', (err as Error)?.name);
      if (errObj?.code) console.error('[PDFDEBUG] Error code:', errObj.code);
      if (errObj?.status) console.error('[PDFDEBUG] HTTP status:', errObj.status);
      setMessages([...newMessages, { role: "model", parts: [{ text: "**Error**: Sorry, I could not connect to the AI." }] }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayTTS = async (text: string, idx: number) => {
    if (isPlaying !== null) return;
    setIsPlaying(idx);
    try {
      const res = await fetch("/api/tts", { 
        method: "POST", 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text })
      });
      const data = await res.json();
      if (data.audio) {
        const mimeType = data.mimeType || 'audio/wav';
        const audioUrl = `data:${mimeType};base64,${data.audio}`;
        const audio = new Audio(audioUrl);
        audio.onended = () => setIsPlaying(null);
        audio.onerror = () => setIsPlaying(null);
        audio.play();
      } else {
        setIsPlaying(null);
      }
    } catch (err: unknown) {
      console.error(err);
      setIsPlaying(null);
    }
  };

  const handleSuggestion = (prompt: string) => {
    setInput(prompt);
  };

  // Collapses multiple consecutive blank lines to a single blank line.
  // IMPORTANT: Do NOT strip blank lines inside fenced code blocks.
  const normalizeMarkdown = (md: string): string => {
    return md
      .replace(/\r\n/g, '\n')
      // Remove <br> and <br/> tags the AI sometimes injects into markdown — they are
      // rendered by marked as literal <br> elements and create huge vertical gaps.
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/\n{3,}/g, '\n\n')        // at most one blank line between blocks
      .replace(/^[ \t]+$/gm, '')          // lines with only whitespace become empty
      .trim();
  };

  // marked v18 sometimes emits whitespace-only text nodes between block elements.
  // html2pdf renders these as visible gaps in the PDF. Strip them from the HTML string.
  const stripStrayWhitespace = (html: string): string => {
    return html
      // Collapse runs of <br> tags down to a single one, then remove solo <br> that
      // appear at the start/end of a block element (a common marked artefact).
      .replace(/(<br\s*\/?>[\s]*){2,}/gi, '<br>')
      .replace(/(<(p|li|blockquote|td|th)[^>]*>)\s*(<br\s*\/?>)+/gi, '$1')
      .replace(/(<br\s*\/?>)+\s*(<\/(p|li|blockquote|td|th)>)/gi, '$2')
      .replace(/>\s+</g, '><')                  // whitespace between tags
      .replace(/\n\s*\n/g, '\n')                // collapse multiple newlines
      .replace(/<p>\s*<\/p>/g, '')              // empty paragraphs
      .replace(/<p>(&nbsp;|\u00A0|\s)*<\/p>/g, '') // non-breaking space paragraphs
      .trim();
  };

  const handleEditDocument = async (pdfData: PdfDocumentData | null) => {
    if (!pdfData || !pdfData.content) return;

    setIsExportingToDocs(true);
    
    const processedContent = await convertQuickChartsToImages(pdfData.content);

    // Do NOT use { breaks: true } — it converts every newline to <br>, creating
    // massive gaps in Google Docs. Use default paragraph-based rendering instead.
    const rawHtml = marked.parse(normalizeMarkdown(processedContent)) as string;
    const htmlContent = stripStrayWhitespace(rawHtml);
    
    const completeHtml = `<main>${htmlContent}</main>`;
    
    try {
      const googleDoc = await createGoogleDocFromHtml(pdfData.title || "Document", completeHtml);
      setActiveGoogleDoc(googleDoc);
      setEditingDocId(googleDoc.documentId);
      setGoogleConnection(getGoogleConnection());
      setGoogleActionStatus(`Saved to Google Drive: ${googleDoc.title}`);
    } catch (e: unknown) {
      console.error(e);
      setGoogleActionStatus(friendlyGoogleError(e));
      alert(friendlyGoogleError(e));
    } finally {
      setIsExportingToDocs(false);
    }
  };

  const handleShareGoogleDoc = async () => {
    if (!activeGoogleDoc) {
      setGoogleActionStatus('Create or open a Google Doc first.');
      return;
    }

    const email = window.prompt('Enter the email address to share this Google Doc with:');
    if (!email) return;

    const canEdit = window.confirm('Allow this person to edit the document? Choose Cancel for view-only access.');
    setGoogleActionStatus('Sharing Google Doc...');
    try {
      await shareGoogleDoc(activeGoogleDoc.documentId, email.trim(), canEdit ? 'writer' : 'reader');
      setGoogleActionStatus(`Shared with ${email.trim()}.`);
    } catch (error) {
      console.error(error);
      setGoogleActionStatus(friendlyGoogleError(error));
    }
  };

  /**
   * Download PDF using Google Docs as the rendering engine.
   *
   * Pipeline:
   *   1. Convert any quickchart blocks to embedded base64 images.
   *   2. Render markdown → clean HTML.
   *   3. Upload the HTML to Google Drive as a Google Doc (same path as
   *      "Create/Edit Google Doc" — rendering already proven to be perfect).
   *   4. Export that doc as PDF via the Drive export API.
   *   5. Silently delete the temporary Google Doc from the user's Drive.
   *
   * This completely bypasses html2canvas / html2pdf.js and therefore has no
   * page-cut, oklch colour, or freeze issues.
   */
  const handleDownloadPdf = async (pdfData: PdfDocumentData | null) => {
    if (!pdfData?.content) return;
    setIsGeneratingPdf(true);
    setGoogleActionStatus('Preparing document for PDF export…');

    let tempDocId: string | null = null;
    try {
      // Step 1 — resolve charts to embedded images
      setGoogleActionStatus('Rendering charts…');
      const processedContent = await convertQuickChartsToImages(pdfData.content);

      // Step 2 — markdown → HTML
      const rawHtml = marked.parse(normalizeMarkdown(processedContent)) as string;
      const htmlContent = stripStrayWhitespace(rawHtml);
      const completeHtml = `<main>${htmlContent}</main>`;

      // Step 3 — create a temporary Google Doc
      setGoogleActionStatus('Creating temporary Google Doc…');
      const googleDoc = await createGoogleDocFromHtml(
        pdfData.title || 'Document',
        completeHtml
      );
      tempDocId = googleDoc.documentId;

      // Step 4 — export the Doc as a PDF blob and trigger download
      setGoogleActionStatus('Exporting PDF from Google Docs…');
      await exportGoogleDoc(googleDoc.documentId, pdfData.title || 'Document', 'pdf');

      setGoogleActionStatus('PDF downloaded successfully.');
    } catch (e: unknown) {
      console.error('[PDFDEBUG] handleDownloadPdf failed:', e);
      setGoogleActionStatus(friendlyGoogleError(e));
    } finally {
      // Step 5 — clean up the temp doc (non-fatal if this fails)
      if (tempDocId) {
        deleteGoogleFile(tempDocId).catch((err) =>
          console.warn('[PDFDEBUG] Temp doc cleanup failed (non-fatal):', err)
        );
      }
      setIsGeneratingPdf(false);
    }
  };

  const markdownComponents = {
    code({ inline, className, children, ...props }: {
      inline?: boolean;
      className?: string;
      children?: ReactNode;
    } & HTMLAttributes<HTMLElement>) {
      const match = /language-(\w+)/.exec(className || '');
      if (!inline && match && match[1] === 'quickchart') {
        try {
          const raw = String(children).trim();
          const config = JSON.parse(raw);
          return <QuickChartImg chartConfig={config} width={800} height={400} />;
        } catch {
          return <code className={className} {...props}>{children}</code>;
        }
      }
      return <code className={className} {...props}>{children}</code>;
    }
  };

  return (
    <div className="absolute inset-0 flex flex-col bg-white dark:bg-[#020617] text-slate-800 dark:text-gray-200 font-sans overflow-hidden text-sm">

      {/* Templates Modal */}
      {showTemplatesModal && (
        <div className="absolute inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowTemplatesModal(false)}>
          <div className="bg-[#020617] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <LayoutTemplate className="w-5 h-5 text-indigo-400" /> Supported Templates
                </h2>
                <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-2 font-bold">100+ Professional Formats Fully Integrated with PDF Generation</p>
              </div>
              <button
                onClick={() => setShowTemplatesModal(false)}
                title="Close templates"
                aria-label="Close templates"
                className="text-gray-500 hover:text-white bg-white/5 p-2 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-sm text-gray-300 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: "Job & Career", items: ["Standard Cover Letter", "Formal Resignation Letter", "Salary Negotiation Letter", "Interview Follow-up Letter"] },
                  { title: "Academic & Education", items: ["Academic Recommendation Letter", "College Admission Letter", "Excuse Letter for Absence", "Teacher Introduction Letter"] },
                  { title: "Business & Corporate", items: ["B2B Sales Proposal", "Partnership Inquiry Letter", "Overdue Payment Reminder", "Client Onboarding Letter"] },
                  { title: "Legal & Public", items: ["Formal Complaint Letter", "Cease and Desist Letter", "Press Release Format", "Grievance Letter"] },
                ].map(cat => (
                  <div key={cat.title} className="bg-white/5 p-4 rounded-xl border border-white/5">
                    <strong className="text-white block mb-2 text-xs">{cat.title}</strong>
                    <ul className="text-gray-400 text-xs space-y-1 ml-4 list-disc">{cat.items.map(i => <li key={i}>{i}</li>)}</ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub Header / Toolbar */}
      {!editingDocId && (
      <div className="h-10 flex items-center justify-between px-5 border-b border-gray-200 dark:border-white/5 shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            title="Go back"
            aria-label="Go back"
            className="text-slate-400 dark:text-gray-500 hover:text-slate-800 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-center gap-1.5 text-slate-800 dark:text-white">
            {activeTab === 'search' ? <Sparkles className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" /> : <File className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />}
            <span className="text-[10px] font-black tracking-widest uppercase">{activeTab === 'search' ? 'Quick Search' : 'Meeting Notes'}</span>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-5 text-[9px] font-bold tracking-widest uppercase text-slate-400 dark:text-gray-500">
          {googleConnection.connected ? (
            <div className="flex items-center gap-2">
              <span className="text-emerald-600 dark:text-emerald-400 max-w-[180px] truncate" title={googleConnection.email || undefined}>
                Google Account Connected{googleConnection.email ? `: ${googleConnection.email}` : ''}
              </span>
              <button
                onClick={handleDisconnectGoogle}
                className="hover:text-slate-800 dark:hover:text-white flex items-center gap-1.5 transition-colors"
                title="Disconnect Google Account"
              >
                <Link2Off className="w-3 h-3" /> Disconnect
              </button>
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleConnectGoogle}
                  className="hover:text-slate-800 dark:hover:text-white flex items-center gap-1.5 transition-colors"
                >
                  <FileText className="w-3 h-3" /> Connect Google Account
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Sign in to create, edit, and share documents directly with Google Drive</p></TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={() => setShowTemplatesModal(true)} className="hover:text-slate-800 dark:hover:text-white flex items-center gap-1.5 transition-colors"><LayoutTemplate className="w-3 h-3" /> Templates</button>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">Browse professional document templates including cover letters, proposals, and recommendation letters</p></TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={handleShareGoogleDoc} className="hover:text-slate-800 dark:hover:text-white flex items-center gap-1.5"><Share2 className="w-3 h-3" /> Share</button>
            </TooltipTrigger>
            <TooltipContent><p className="text-[10px]">Share the active Google Doc with others via email. Requires a document to be created first.</p></TooltipContent>
          </Tooltip>
        </div>
      </div>
      )}

      {!editingDocId && googleActionStatus && (
        <div className="px-5 py-2 border-b border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-white/[0.03] text-[10px] font-bold tracking-widest uppercase text-slate-400 dark:text-gray-400">
          {googleActionStatus}
        </div>
      )}

      {/* Main Chat Area */}
      <main className={`min-h-0 flex-1 scroll-smooth w-full flex flex-col ${editingDocId ? 'bg-white items-stretch overflow-hidden' : 'items-center overflow-y-auto'}`}>
        {editingDocId ? (
          <div className="w-full flex-1 min-h-0 flex flex-col h-full relative">
            <button
              onClick={() => setEditingDocId(null)}
              title="Back to chat"
              aria-label="Back to chat"
              className="absolute left-3 top-3 z-20 h-8 w-8 flex items-center justify-center rounded-full bg-[#0a1628]/90 text-white border border-white/10 shadow-lg hover:bg-[#111c33]"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
              <a 
                href={`https://docs.google.com/document/d/${editingDocId}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                className="h-8 px-4 flex items-center justify-center rounded-full bg-blue-600/90 text-[11px] font-bold tracking-wider uppercase text-white border border-blue-500/30 shadow-lg hover:bg-blue-700 transition-colors gap-2"
                title="Open in Google Docs"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Open in Docs</span>
              </a>
            </div>
            <div className="absolute right-3 top-3 z-20 flex items-center gap-2">
              <button
                onClick={() => setIsDocFullscreen((value) => !value)}
                title={isDocFullscreen ? "Exit fullscreen" : "Fullscreen"}
                aria-label={isDocFullscreen ? "Exit fullscreen" : "Fullscreen"}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-[#0a1628]/90 text-white border border-white/10 shadow-lg hover:bg-[#111c33]"
              >
                {isDocFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
            <div className="w-full flex-1 min-h-0 relative bg-white">
              <iframe src={`https://docs.google.com/document/d/${editingDocId}/edit?embedded=true`} className="absolute inset-0 h-full w-full border-0 block" title="Google Docs Editor" />
            </div>
          </div>
        ) : messages.length === 0 ? (
          activeTab === 'search' ? (
            <div className="max-w-3xl mx-auto p-4 sm:p-6 w-full flex-1 flex flex-col justify-center">
              <div className="text-center px-4">
                <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
                  <Search className="w-5 h-5 text-indigo-400" />
                </div>
                <h2 className="text-lg font-black uppercase tracking-tighter text-white mb-1.5">How can I help you teach today?</h2>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 max-w-sm mx-auto mb-5">
                  Lesson Planning, Content Creation, Interactive Resources, or Student Support.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-w-lg mx-auto text-left">
                  {[
                    "Draft a Form 3 Biology lesson plan on cells...",
                    "Create an escape room activity for fractions",
                    "Write a professional email to a struggling student's parents",
                    "Create for me a 3 page pdf on the importance of preserving nature"
                  ].map((prompt, i) => (
                    <Tooltip key={i}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleSuggestion(prompt)}
                          className="p-2.5 text-left text-[10px] font-bold uppercase tracking-wider border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.02] hover:bg-gray-100 dark:hover:bg-white/5 hover:border-indigo-400/40 transition-all text-slate-500 dark:text-gray-500 hover:text-slate-800 dark:hover:text-gray-300"
                        >
                          {prompt}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent><p className="text-[10px]">Click to load this example prompt. Edit it before sending.</p></TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto p-4 sm:p-6 w-full flex-1 flex flex-col justify-center">
              <div className="text-center px-4">
                <div className="w-12 h-12 bg-indigo-50 dark:bg-white/5 border border-indigo-200 dark:border-white/10 flex items-center justify-center mx-auto mb-4">
                  <File className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h2 className="text-lg font-black uppercase tracking-tighter text-slate-900 dark:text-white mb-1.5">Meeting Notes</h2>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-gray-500 max-w-sm mx-auto mb-5">
                  Paste a raw transcript and get structured meeting notes.
                </p>
              </div>
            </div>
          )
        ) : (
          <div className="w-full max-w-3xl mx-auto p-4 md:p-6 flex flex-col gap-6">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex gap-4 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "model" && (
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-white/5 border border-indigo-200 dark:border-white/10 flex flex-shrink-0 items-center justify-center mt-1 text-indigo-600 dark:text-indigo-400">
                    <Sparkles className="w-4 h-4" />
                  </div>
                )}
                {msg.role === "user" ? (
                  <>
                    <div className="bg-indigo-600 text-white rounded-sm px-5 py-3.5 max-w-[70%] text-sm font-medium leading-relaxed">
                      {(() => {
                        const textPart = getTextPart(msg);
                        if (textPart) return textPart;
                        const audioPart = msg.parts.find(p => 'inlineData' in p) as { inlineData: { data: string; mimeType: string } } | undefined;
                        if (audioPart) return <AudioMessageBubble base64Data={audioPart.inlineData.data} mimeType={audioPart.inlineData.mimeType} />;
                        return "Audio Message";
                      })()}
                    </div>
                    <div className="w-8 h-8 rounded-sm bg-white text-[#020617] flex flex-shrink-0 items-center justify-center mt-1">
                      <span className="font-bold text-xs">V</span>
                    </div>
                  </>
                ) : (
                  <div className="max-w-[90%] text-sm w-full">
                    {(() => {
                      const partText = getTextPart(msg);
                      const parsedData = parseAssistantPayload(partText);
                      if (!parsedData) {
                        return (
                          <div className="bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/8 rounded-xl px-5 py-4 shadow-sm">
                            <div className="flex justify-between items-start">
                              <div className="text-slate-700 dark:text-gray-300 prose prose-p:leading-relaxed dark:prose-invert max-w-none text-sm">
                                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>{partText}</ReactMarkdown>
                              </div>
                              <button
                                onClick={() => handlePlayTTS(partText, idx)}
                                disabled={isPlaying !== null}
                                title="Read message aloud"
                                aria-label="Read message aloud"
                                className="text-slate-400 dark:text-gray-500 hover:text-indigo-500 dark:hover:text-white transition-colors p-2"
                              >
                                {isPlaying === idx ? <Loader2 className="w-4 h-4 animate-spin text-indigo-400" /> : <Volume2 className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/8 rounded-xl overflow-hidden shadow-sm">
                          <div className="flex flex-col gap-6 mt-0">
                            {parsedData.finalAnswer && (
                              <div className="p-5 border-b border-gray-100 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] relative group">
                                <div className="flex justify-between items-start">
                                  <h3 className="text-emerald-600 dark:text-emerald-400 text-[11px] font-bold tracking-widest uppercase mb-3">Final Answer</h3>
                                  <button
                                    onClick={() => parsedData.finalAnswer && handlePlayTTS(parsedData.finalAnswer, idx)}
                                    disabled={isPlaying !== null}
                                    title="Read Aloud"
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 dark:text-gray-500 hover:text-emerald-500 dark:hover:text-emerald-400 p-1 rounded"
                                  >
                                    {isPlaying === idx ? <Loader2 className="w-4 h-4 animate-spin text-emerald-400" /> : <Volume2 className="w-4 h-4" />}
                                  </button>
                                </div>
                                <div className="text-slate-800 dark:text-gray-100 leading-relaxed font-medium">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                                    {parsedData.finalAnswer}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            )}

                            {parsedData.explanation && (
                              <div className="p-5">
                                <h3 className="text-slate-900 dark:text-white text-[11px] font-bold tracking-widest uppercase mb-4">Step-by-Step Explanation</h3>
                                <div className="text-slate-700 dark:text-gray-300 prose prose-p:leading-relaxed dark:prose-invert prose-pre:bg-gray-50 dark:prose-pre:bg-white/5 prose-pre:border prose-pre:border-gray-200 dark:prose-pre:border-white/10 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline max-w-none text-sm">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                                    {parsedData.explanation}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            )}
                            {/* Only show document buttons when the AI explicitly generated a PDF document */}
                            {parsedData.pdfDocument?.content && (() => {
                              const pdfDocument = parsedData.pdfDocument as PdfDocumentData;
                              return (
                                <div className="p-5 border-t border-gray-100 dark:border-white/[0.06] flex flex-col items-start gap-4">
                                  <p className="text-sm text-slate-600 dark:text-gray-300 italic font-medium">
                                    {`Your document "${pdfDocument.title ?? 'document'}" is ready. Download it as a PDF or open it in Google Docs.`}
                                  </p>
                                  <div className="flex items-center gap-3 w-full flex-wrap">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button 
                                          onClick={() => handleEditDocument(pdfDocument)}
                                          disabled={isExportingToDocs}
                                          className="flex items-center gap-2 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/10 text-slate-800 dark:text-white px-5 py-2.5 rounded shadow-sm transition-colors font-semibold text-sm disabled:opacity-50"
                                        >
                                          {isExportingToDocs ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />}
                                          {isExportingToDocs ? 'Creating Google Doc...' : 'Create/Edit Google Doc'}
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent><p className="text-[10px]">Export this AI-generated document to Google Docs for rich editing and collaboration</p></TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button 
                                          onClick={() => handleDownloadPdf(pdfDocument)}
                                          disabled={isGeneratingPdf}
                                          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded shadow-sm transition-colors font-semibold text-sm disabled:opacity-50"
                                        >
                                          {isGeneratingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                          {isGeneratingPdf ? "Generating PDF..." : "Download PDF"}
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent><p className="text-[10px]">Download this document as a professionally formatted PDF</p></TooltipContent>
                                    </Tooltip>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {parsedData.references && parsedData.references.length > 0 && (
                            <div>
                              <h3 className="text-slate-900 dark:text-white text-[11px] font-bold tracking-widest uppercase mb-3">References</h3>
                              <ol className="space-y-2 max-w-full">
                                {parsedData.references.map((ref: string, i: number) => {
                                  const parsed = parseReference(ref, i + 1);
                                  return (
                                    <li key={i} className="flex gap-3 items-start text-sm leading-relaxed text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-gray-200 transition-colors">
                                      <span className="shrink-0 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 text-[10px] font-bold tracking-wider text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 rounded">
                                        [{i + 1}]
                                      </span>
                                      <div className="min-w-0 flex-1 break-words">
                                        <span>{parsed.title}</span>
                                        {parsed.url && (
                                          <>
                                            <span className="text-slate-400 dark:text-gray-600 mx-1">—</span>
                                            <a
                                              href={parsed.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 underline underline-offset-2 break-all"
                                            >
                                              {parsed.url}
                                            </a>
                                          </>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ol>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex gap-4 justify-start">
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex flex-shrink-0 items-center justify-center mt-1">
                  <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
                </div>
                <div className="flex items-center px-2 py-2">
                  <span className="text-xs text-gray-500 font-bold tracking-widest uppercase animate-pulse">Analyzing request...</span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} className="h-4" />
          </div>
        )}
      </main>

      {/* Bottom Input */}
      {!editingDocId && (
      <div className="flex-none px-4 pb-4 pt-6 bg-gradient-to-t from-gray-50 dark:from-[#020617] via-gray-50/80 dark:via-[#020617]/80 to-transparent z-10">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-2 items-end bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/10 transition-all focus-within:border-blue-500/50 dark:focus-within:border-blue-500/30 focus-within:shadow-[0_0_0_2px_rgba(59,130,246,0.1)]">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
              placeholder={activeTab === 'search' ? "Ask for a lesson plan, activity, or student resource..." : "Paste your raw transcript here to get actionable meeting notes..."}
              className="flex-1 bg-transparent border-none outline-none text-sm text-slate-800 dark:text-zinc-200 placeholder:text-slate-400 dark:placeholder:text-slate-600 min-h-[2.25rem] py-2.5 px-3"
              disabled={isLoading}
            />
            <div className="flex items-center gap-1 pr-1.5 pb-1.5 shrink-0">
              <button
                type="button"
                title={isRecording ? "Stop and send recording" : "Start voice recording"}
                aria-label={isRecording ? "Stop recording" : "Start voice recording"}
                onClick={toggleRecording}
                className={`h-8 w-8 flex items-center justify-center transition-all ${
                  isRecording
                    ? 'text-white bg-red-500 hover:bg-red-600 rounded-full animate-pulse shadow-lg shadow-red-500/30'
                    : 'text-slate-400 dark:text-slate-600 hover:text-blue-500 dark:hover:text-blue-400'
                }`}
              >
                {isRecording ? <Square className="w-3 h-3 fill-current" /> : <Mic className="w-3.5 h-3.5" />}
              </button>
              <button
                type="button"
                title="Send message"
                aria-label="Send message"
                disabled={!input.trim() || isLoading}
                onClick={() => handleSubmit()}
                className="h-8 w-8 flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-700 mt-2 text-center">
            AI responses may be inaccurate — always verify critical content.
          </p>
        </div>
      </div>
      )}
    </div>
  );
}
